/**
 * Question Bank API Service
 * 
 * Manages feedback questions and templates for HR admins.
 */

import { apiClient } from '../apiClient'

// ==================== Types ====================

export type QuestionType = 'TEXT' | 'RATING_SCALE' | 'LIKERT' | 'MULTIPLE_CHOICE' | 'MULTI_SELECT' | 'YES_NO' | 'RANKING'
export type QuestionCategory = 'COMMUNICATION' | 'LEADERSHIP' | 'TECHNICAL' | 'COLLABORATION' | 'DELIVERY' | 'INNOVATION' | 'DEVELOPMENT' | 'VALUES' | 'STUDENT_ENGAGEMENT'
export type FeedbackContext = 'MATRIX' | 'UPWARD_360' | 'EMPLOYEE_REQUESTED' | 'PEER' | 'PROJECT'
export type StakeholderRole = 'CAMPUS_GM' | 'PROJECT_MANAGER' | 'FACULTY_MEMBER' | 'DEPARTMENT_HEAD' | 'PEER' | 'DIRECT_REPORT' | 'EXTERNAL' | 'STUDENT'

export interface QuestionConfig {
  min?: number
  max?: number
  labels?: Record<string, string>
  options?: string[]
  min_selections?: number
  max_selections?: number
}

export interface Question {
  id: string
  question_text: string
  question_code: string
  question_type: QuestionType
  category: QuestionCategory
  question_config?: QuestionConfig
  applicable_job_families?: string[]
  applicable_functional_areas?: string[]
  applicable_feedback_contexts: string[]
  applicable_stakeholder_roles?: string[]
  applicable_campuses?: string[]
  is_required: boolean
  is_active: boolean
  display_order?: number
  helper_text?: string
  admin_notes?: string
  usage_count: number
  last_used_at?: string
  source_system?: string
  source_reference?: string
  created_by: string
  updated_by?: string
  created_at: string
  updated_at: string
}

export interface QuestionCreate {
  question_text: string
  /**
   * Optional — the backend auto-generates a category-prefixed sequential
   * code (e.g. `leadership_Q07`) when this is omitted. Callers should
   * only send a code when porting existing IDs (e.g. import, migration).
   */
  question_code?: string
  question_type: QuestionType
  category: QuestionCategory
  question_config?: QuestionConfig
  applicable_job_families?: string[]
  applicable_functional_areas?: string[]
  applicable_feedback_contexts: string[]
  applicable_stakeholder_roles?: string[]
  applicable_campuses?: string[]
  is_required?: boolean
  display_order?: number
  helper_text?: string
  admin_notes?: string
  source_system?: string
  source_reference?: string
}

export interface QuestionUpdate {
  question_text?: string
  question_type?: QuestionType
  category?: QuestionCategory
  question_config?: QuestionConfig
  applicable_job_families?: string[]
  applicable_functional_areas?: string[]
  applicable_feedback_contexts?: string[]
  applicable_stakeholder_roles?: string[]
  applicable_campuses?: string[]
  is_required?: boolean
  is_active?: boolean
  display_order?: number
  helper_text?: string
  admin_notes?: string
}

export interface TemplateQuestion {
  id: string
  template_id: string
  question_id: string
  is_required: boolean
  display_order: number
  custom_question_text?: string
  custom_helper_text?: string
  question: Question
}

/**
 * Distribution model for a feedback template.
 * - `in_the_moment`: per-request stakeholder feedback (default behaviour)
 * - `annual_campaign`: bulk upward feedback campaigns. Triggers
 *   respondent-only response visibility and surfaces in the Campaigns
 *   admin tab. See P1.1 migration.
 */
export type FeedbackTemplateType = 'in_the_moment' | 'annual_campaign'

export interface FeedbackTemplate {
  id: string
  template_name: string
  template_code: string
  description?: string
  template_type?: FeedbackTemplateType
  feedback_context: FeedbackContext
  primary_stakeholder_role?: StakeholderRole
  applicable_job_families?: string[]
  applicable_functional_areas?: string[]
  applicable_campuses?: string[]
  allow_custom_question: boolean
  estimated_completion_time?: number
  is_active: boolean
  is_default: boolean
  usage_count: number
  last_used_at?: string
  created_by: string
  updated_by?: string
  created_at: string
  updated_at: string
  template_questions?: TemplateQuestion[]
}

export interface TemplateQuestionCreate {
  question_id: string
  is_required: boolean
  display_order: number
  custom_question_text?: string
  custom_helper_text?: string
}

export interface TemplateCreate {
  template_name: string
  template_code: string
  description?: string
  template_type?: FeedbackTemplateType
  feedback_context: FeedbackContext
  primary_stakeholder_role?: StakeholderRole
  applicable_job_families?: string[]
  applicable_functional_areas?: string[]
  applicable_campuses?: string[]
  allow_custom_question?: boolean
  estimated_completion_time?: number
  is_default?: boolean
  questions?: TemplateQuestionCreate[]
}

export interface TemplateUpdate {
  template_name?: string
  description?: string
  template_type?: FeedbackTemplateType
  feedback_context?: FeedbackContext
  primary_stakeholder_role?: StakeholderRole
  applicable_job_families?: string[]
  applicable_functional_areas?: string[]
  applicable_campuses?: string[]
  allow_custom_question?: boolean
  estimated_completion_time?: number
  is_active?: boolean
  is_default?: boolean
  questions?: TemplateQuestionCreate[]
}

export interface QuestionFilters {
  search?: string
  question_type?: QuestionType
  category?: QuestionCategory
  feedback_context?: string
  job_family?: string
  functional_area?: string
  is_active?: boolean
  skip?: number
  limit?: number
}

export interface TemplateFilters {
  search?: string
  /** The API takes the lower-case value ('matrix'). */
  feedback_context?: FeedbackContext | Lowercase<FeedbackContext>
  stakeholder_role?: StakeholderRole
  template_type?: FeedbackTemplateType
  job_family?: string
  is_active?: boolean
  is_default?: boolean
  skip?: number
  limit?: number
}

export interface PaginatedResponse<T> {
  items: T[]
  total: number
  skip: number
  limit: number
}

/**
 * The question bank's categories and their labels (renaming them, #16, is
 * paused). Respondents see the labels as section headings.
 */
export const QUESTION_CATEGORIES: { value: QuestionCategory; label: string }[] = [
  { value: 'COMMUNICATION', label: 'Communication' },
  { value: 'LEADERSHIP', label: 'Leadership' },
  { value: 'TECHNICAL', label: 'Technical' },
  { value: 'COLLABORATION', label: 'Collaboration' },
  { value: 'DELIVERY', label: 'Delivery' },
  { value: 'INNOVATION', label: 'Innovation' },
  { value: 'DEVELOPMENT', label: 'Development' },
  { value: 'VALUES', label: 'Values' },
  { value: 'STUDENT_ENGAGEMENT', label: 'Student Engagement' },
]

/** A category's label. The API sends the lower-case value ('student_engagement'). */
export function questionCategoryLabel(category: string): string {
  const value = category.toUpperCase()
  const known = QUESTION_CATEGORIES.find((c) => c.value === value)
  if (known) return known.label
  const words = category.replace(/_/g, ' ').toLowerCase()
  return words.charAt(0).toUpperCase() + words.slice(1)
}

/** The API serves at most 100 rows a page. */
const PAGE_SIZE = 100

/**
 * Fetch page after page until every row is in. One request used to stop at
 * the first 100, so anything past it was silently missing (HR UAT, Oct 2026).
 */
async function fetchEveryPage<T>(
  fetchPage: (skip: number, limit: number) => Promise<PaginatedResponse<T> | T[]>,
): Promise<PaginatedResponse<T>> {
  const items: T[] = []
  for (;;) {
    const page = await fetchPage(items.length, PAGE_SIZE)
    // An older API answered with a bare array: that is everything there is.
    if (Array.isArray(page)) {
      items.push(...page)
      break
    }
    const rows = page?.items || []
    items.push(...rows)
    if (rows.length < PAGE_SIZE || items.length >= (page?.total ?? 0)) break
  }
  return { items, total: items.length, skip: 0, limit: items.length }
}

// ==================== Question Bank Service ====================

const questionBankService = {
  // ==================== Questions ====================
  
  async listQuestions(filters?: QuestionFilters): Promise<PaginatedResponse<Question>> {
    const params = new URLSearchParams()
    if (filters?.search) params.append('search', filters.search)
    if (filters?.question_type) params.append('question_type', filters.question_type)
    if (filters?.category) params.append('category', filters.category)
    if (filters?.feedback_context) params.append('feedback_context', filters.feedback_context)
    if (filters?.is_active !== undefined) params.append('is_active', String(filters.is_active))
    if (filters?.skip !== undefined) params.append('skip', String(filters.skip))
    if (filters?.limit !== undefined) params.append('limit', String(filters.limit))
    
    const response = await apiClient.get<PaginatedResponse<Question>>(
      `/api/v1/admin/question-bank/questions?${params.toString()}`
    )
    return response.data
  },

  /** Every question matching the filters, however many pages that takes. */
  async listAllQuestions(
    filters?: Omit<QuestionFilters, 'skip' | 'limit'>,
  ): Promise<PaginatedResponse<Question>> {
    return fetchEveryPage((skip, limit) => questionBankService.listQuestions({ ...filters, skip, limit }))
  },

  async getQuestion(questionId: string): Promise<Question> {
    const response = await apiClient.get<Question>(`/api/v1/admin/question-bank/questions/${questionId}`)
    return response.data
  },

  async createQuestion(question: QuestionCreate): Promise<Question> {
    const response = await apiClient.post<Question>('/api/v1/admin/question-bank/questions', question)
    return response.data
  },

  async updateQuestion(questionId: string, update: QuestionUpdate): Promise<Question> {
    const response = await apiClient.put<Question>(`/api/v1/admin/question-bank/questions/${questionId}`, update)
    return response.data
  },

  async deleteQuestion(questionId: string): Promise<void> {
    await apiClient.delete(`/api/v1/admin/question-bank/questions/${questionId}`)
  },

  /**
   * Duplicate a question. `newCode` is optional — when omitted, the
   * backend gives the copy the next code in its category, as it does a new
   * question (`leadership_Q08`).
   */
  async duplicateQuestion(questionId: string, newCode?: string): Promise<Question> {
    const path = newCode
      ? `/api/v1/admin/question-bank/questions/${questionId}/duplicate?new_code=${encodeURIComponent(newCode)}`
      : `/api/v1/admin/question-bank/questions/${questionId}/duplicate`
    const response = await apiClient.post<Question>(path)
    return response.data
  },

  /**
   * Export the question bank as a Blob suitable for download.
   * Default format is xlsx — what HR admins actually want to open in
   * Excel and edit. Pass `'json'` for the engineer-friendly envelope
   * (e.g. for scripted migrations).
   */
  async exportQuestions(format: 'xlsx' | 'json' = 'xlsx'): Promise<{ blob: Blob; filename: string }> {
    const response = await apiClient.get(
      `/api/v1/admin/question-bank/questions/export?format=${format}`,
      { responseType: format === 'xlsx' ? 'blob' : 'json' },
    )
    const stamp = new Date().toISOString().slice(0, 10)
    if (format === 'xlsx') {
      // axios returns the raw bytes when responseType === 'blob'.
      // Try to honour the server's Content-Disposition filename if it
      // surfaces, but fall back to the predictable timestamp-based name.
      const cd = (response.headers as any)?.['content-disposition'] as string | undefined
      const m = cd?.match(/filename="?([^"]+)"?/)
      return {
        blob: response.data as Blob,
        filename: m?.[1] || `question-bank-${stamp}.xlsx`,
      }
    }
    // JSON path — wrap the parsed body back into a Blob so callers have
    // a single uniform interface, regardless of format.
    const text = JSON.stringify(response.data, null, 2)
    return {
      blob: new Blob([text], { type: 'application/json' }),
      filename: `question-bank-${stamp}.json`,
    }
  },

  /**
   * Bulk-upsert questions from an uploaded file (xlsx or json).
   * Backend updates rows whose `question_code` matches an existing entry
   * and creates the rest, so a re-imported export is the way to bulk-edit
   * questions. Returns `{created, updated, skipped, errors}`.
   */
  async importQuestions(file: File): Promise<{
    created: number
    updated: number
    skipped: number
    errors: { index: number; reason: string }[]
  }> {
    const form = new FormData()
    form.append('file', file)
    const response = await apiClient.post<{
      created: number
      updated: number
      skipped: number
      errors: { index: number; reason: string }[]
    }>(
      '/api/v1/admin/question-bank/questions/import',
      form,
      // Let the browser set the multipart boundary automatically by NOT
      // sending an explicit Content-Type. axios respects this when the
      // body is a FormData instance.
      { headers: { 'Content-Type': 'multipart/form-data' } },
    )
    return response.data
  },

  // ==================== Templates ====================

  async listTemplates(filters?: TemplateFilters): Promise<PaginatedResponse<FeedbackTemplate>> {
    const params = new URLSearchParams()
    if (filters?.search) params.append('search', filters.search)
    if (filters?.feedback_context) params.append('feedback_context', filters.feedback_context)
    if (filters?.stakeholder_role) params.append('stakeholder_role', filters.stakeholder_role)
    if (filters?.template_type) params.append('template_type', filters.template_type)
    if (filters?.is_active !== undefined) params.append('is_active', String(filters.is_active))
    if (filters?.is_default !== undefined) params.append('is_default', String(filters.is_default))
    if (filters?.skip !== undefined) params.append('skip', String(filters.skip))
    if (filters?.limit !== undefined) params.append('limit', String(filters.limit))
    
    const response = await apiClient.get<PaginatedResponse<FeedbackTemplate>>(
      `/api/v1/admin/question-bank/templates?${params.toString()}`
    )
    return response.data
  },

  /** Every template matching the filters, however many pages that takes. */
  async listAllTemplates(
    filters?: Omit<TemplateFilters, 'skip' | 'limit'>,
  ): Promise<PaginatedResponse<FeedbackTemplate>> {
    return fetchEveryPage((skip, limit) => questionBankService.listTemplates({ ...filters, skip, limit }))
  },

  async getTemplate(templateId: string, includeQuestions = true): Promise<FeedbackTemplate> {
    const response = await apiClient.get<FeedbackTemplate>(
      `/api/v1/admin/question-bank/templates/${templateId}?include_questions=${includeQuestions}`
    )
    return response.data
  },

  async createTemplate(template: TemplateCreate): Promise<FeedbackTemplate> {
    const response = await apiClient.post<FeedbackTemplate>('/api/v1/admin/question-bank/templates', template)
    return response.data
  },

  async updateTemplate(templateId: string, update: TemplateUpdate): Promise<FeedbackTemplate> {
    const response = await apiClient.put<FeedbackTemplate>(
      `/api/v1/admin/question-bank/templates/${templateId}`,
      update
    )
    return response.data
  },

  async deleteTemplate(templateId: string): Promise<void> {
    await apiClient.delete(`/api/v1/admin/question-bank/templates/${templateId}`)
  },

  /**
   * Export feedback templates (with their question links) as a downloadable
   * blob. Default format is xlsx — two sheets (`Templates` + `TemplateQuestions`)
   * so admins can edit composition and reorder in Excel without losing
   * referential integrity. Pass `'json'` for the engineer envelope.
   */
  async exportTemplates(format: 'xlsx' | 'json' = 'xlsx'): Promise<{ blob: Blob; filename: string }> {
    const response = await apiClient.get(
      `/api/v1/admin/question-bank/templates/export?format=${format}`,
      { responseType: format === 'xlsx' ? 'blob' : 'json' },
    )
    const stamp = new Date().toISOString().slice(0, 10)
    if (format === 'xlsx') {
      const cd = (response.headers as any)?.['content-disposition'] as string | undefined
      const m = cd?.match(/filename="?([^"]+)"?/)
      return {
        blob: response.data as Blob,
        filename: m?.[1] || `feedback-templates-${stamp}.xlsx`,
      }
    }
    const text = JSON.stringify(response.data, null, 2)
    return {
      blob: new Blob([text], { type: 'application/json' }),
      filename: `feedback-templates-${stamp}.json`,
    }
  },

  /**
   * Bulk-import feedback templates from a two-sheet xlsx (or JSON envelope).
   * Backend skips templates whose `template_code` already exists, so re-importing
   * an unedited export is idempotent. Returns `{created, skipped, errors}`.
   */
  async importTemplates(file: File): Promise<{
    created: number
    updated?: number
    skipped: number
    errors: { index: number; reason: string }[]
  }> {
    const form = new FormData()
    form.append('file', file)
    const response = await apiClient.post<{
      created: number
      updated?: number
      skipped: number
      errors: { index: number; reason: string }[]
    }>(
      '/api/v1/admin/question-bank/templates/import',
      form,
      { headers: { 'Content-Type': 'multipart/form-data' } },
    )
    return response.data
  },
}

export default questionBankService
